// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — three modes that PLAY differently (principle #14), not three dials.
//   Trek     — a medium route (par 3–5): optimise a short crossing.
//   Odyssey  — a long haul (par 6–11): strategic routing across a whole landmass.
//   Chain    — no destination: how long a non-repeating border-chain can you build?

import { ALL, type Country, type Pool } from './data';

export type Objective = 'route' | 'chain';

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  objective: Objective;
  /** Inclusive par (BFS optimal hop) range for a route mode. */
  parMin: number;
  parMax: number;
  /** Lives (misfires allowed before the round ends). */
  lives: number;
  /** Race timer (ms). Solo has no clock. */
  timeMs: number;
}

export const MODES: Record<string, Mode> = {
  trek: {
    id: 'trek',
    name: 'Trek',
    blurb:
      'A medium crossing between two countries. Hop neighbour to neighbour and beat the game’s shortest route. Great for a quick round.',
    objective: 'route',
    parMin: 3,
    parMax: 5,
    lives: 5,
    timeMs: 120_000,
  },
  odyssey: {
    id: 'odyssey',
    name: 'Odyssey',
    blurb:
      'A long haul across a whole landmass — six to eleven hops at best. Plan the corridor, not just the next step. A real test of the map.',
    objective: 'route',
    parMin: 6,
    parMax: 11,
    lives: 6,
    timeMs: 180_000,
  },
  chain: {
    id: 'chain',
    name: 'Chain',
    blurb:
      'No destination. From the start, keep naming a NEW bordering country as long as you can — how far can the chain reach before you’re stuck?',
    objective: 'chain',
    parMin: 0,
    parMax: 0,
    lives: 4,
    timeMs: 120_000,
  },
};

export const MODE_IDS = ['trek', 'odyssey', 'chain'] as const;
export const DEFAULT_MODE_ID = 'trek';

/** Validate an id off the wire and fall back rather than hand `undefined` on. */
export function modeOf(id: string | null | undefined): Mode {
  if (id && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE_ID];
}

/** Pools a country may be NAMED from — every real country is a legal hop. */
const GUESS_POOLS: Pool[] = ['core', 'extra', 'territory'];

let _guessable: Set<string> | null = null;
/** Ids legal to name / search in any mode (used to validate wire input). */
export function guessableIds(): Set<string> {
  if (!_guessable) _guessable = new Set(ALL.filter((c) => GUESS_POOLS.includes(c.pool)).map((c) => c.id));
  return _guessable;
}

let _guessPool: Country[] | null = null;
export function guessPool(): Country[] {
  if (!_guessPool) _guessPool = ALL.filter((c) => GUESS_POOLS.includes(c.pool));
  return _guessPool;
}
